import { Injectable } from '@angular/core';
import { ExcelGeneratorData } from 'src/app/dto/excel-generation-data.dto';
import { Order } from 'src/app/dto/order.dto';
import { RtsVehicle } from 'src/app/dto/get-rts-result.dto';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { XlsxTemplateFillerServiceService } from './xlsx-template-filler-service.service';
import { DateTimeService } from './date-time.service';
import { OrdersMergeService } from '../order/orders-merge.service';
import { DocumentFieldFormatter } from '../../utils/document-field-formatter.util';

@Injectable({
  providedIn: 'root'
})
export class RtsGeneratorService {
  private readonly ttnTemplatePath: string = 'assets/docs/rts-template.xlsx';

  constructor(
    private xlsxTemplateFillerServiceService: XlsxTemplateFillerServiceService,
    private dateTimeService: DateTimeService,
    private ordersMergeService: OrdersMergeService
  ) {}

  async getTtnByOrderId(orderId: number, dayIndex: number) {
    const indexDBOrderRecord = await firstValueFrom(
      this.ordersMergeService.getCurrentLocalOrder(orderId)
    );
    if (indexDBOrderRecord?.Order) {
      const order = new Order(indexDBOrderRecord.Order as Order);
      if (order && order.registry.days[dayIndex]) {
        await this.getTtn(order.registry.days[dayIndex].vehicles, order);
      }
    } else {
      Sentry.captureException('Ошибка получения ТТН по orderID для полки');
    }
  }

  async getTtnByShipmentDate(orderId: number, shipmentDate: string) {
    const indexDBOrderRecord = await firstValueFrom(
      this.ordersMergeService.getCurrentLocalOrder(orderId)
    );

    if (indexDBOrderRecord?.Order) {
      const order = new Order(indexDBOrderRecord.Order as Order);

      // Нормализуем входящую дату (убираем время, если есть)
      const normalizedInputDate = shipmentDate.split(' ')[0];

      // Найти день по дате отгрузки
      const targetDay = order.registry.days.find(day => {
        // Извлекаем только дату без времени для сравнения
        const dayDate = day.shipmentDate.split(' ')[0];
        return dayDate === normalizedInputDate;
      });

      if (targetDay) {
        await this.getTtn(targetDay.vehicles, order);
      } else {
        throw new Error(`День с датой отгрузки ${normalizedInputDate} не найден`);
      }
    } else {
      Sentry.captureException('Ошибка получения ТТН по orderID для полки');
    }
  }

  async getTtn(vehicleArr: RtsVehicle[], order: Order): Promise<void> {
    const data = await this.generateRTSDataStructure(vehicleArr, order);
    await this.xlsxTemplateFillerServiceService.generateAndOpenNewExcelFile(
      this.ttnTemplatePath,
      data,
      `РТС-${order.id}`
    );
  }

  private async generateRTSDataStructure(
    vehicleArr: RtsVehicle[],
    order: Order
  ) {
    const generateRtsData: ExcelGeneratorData = {
      cellDataArray: [],
      additionalOptions: {
        removeCellPoints: [
          {
            pageNumber: 1,
            startRow: 5,
            endRow: 57
          }
        ]
      }
    };

    generateRtsData.cellDataArray.push({
      address: 'C2',
      value: `${order.id}`,
      name: 'order-id',
      pageNumber: 1
    });

    generateRtsData.cellDataArray.push({
      address: 'C3',
      value: '',
      name: 'loading-date',
      pageNumber: 1
    });

    let startIndex = 5;
    let catListNumber = 1;

    vehicleArr.forEach(car => {
      generateRtsData.cellDataArray.push({
        address: `A${startIndex}`,
        value: `${catListNumber}`,
        name: 'car-list-number',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `B${startIndex}`,
        value: '1',
        name: 'default-value',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `C${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.model),
        name: 'car-name',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `D${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.registrationNumber),
        name: 'car-number',
        pageNumber: 1
      });
      generateRtsData.cellDataArray.push({
        address: `E${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.trailerRegistrationNumber),
        name: 'trailer-number',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `F${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.driverName),
        name: 'driver-name',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `G${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.driverInn),
        name: 'driver-inn',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `H${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.driverLicenseNumber),
        name: 'driver-license',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `I${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.driverPassport),
        name: 'driver-documents',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `J${startIndex}`,
        value: DocumentFieldFormatter.safeValue(order.forwarder?.orgName),
        name: 'forwarder-name',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `K${startIndex}`,
        value: DocumentFieldFormatter.safeValue(order.forwarder?.address),
        name: 'forwarder-address',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `L${startIndex}`,
        value: DocumentFieldFormatter.safeValue(order.forwarder?.inn),
        name: 'forwarder-inn',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `M${startIndex}`,
        value: DocumentFieldFormatter.safeValue(order.forwarder?.kpp),
        name: 'forwarder-kpp',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `N${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierName),
        name: 'carrier-name',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `O${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierBankDetails),
        name: 'carrier-bank-details',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `P${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierAddress),
        name: 'carrier-address',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `Q${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierInn),
        name: 'carrier-inn',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `R${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierOgrn),
        name: 'carrier-orgrnip',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `S${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierOno),
        name: 'carrier-ono',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `T${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierEmail),
        name: 'carrier-email',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `U${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.driverPhone),
        name: 'driver-phone',
        pageNumber: 1
      });
      generateRtsData.cellDataArray.push({
        address: `V${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierKpp),
        name: 'carrier-kpp',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `W${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierPassportNumber),
        name: 'carrier-passport',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `X${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierPassportDetails),
        name: 'carrier-passport-details',
        pageNumber: 1
      });

      generateRtsData.cellDataArray.push({
        address: `Y${startIndex}`,
        value: DocumentFieldFormatter.safeValue(car.carrierPassportDate),
        name: 'carrier-passport-date',
        pageNumber: 1
      });

      catListNumber += 1;
      startIndex += 1;
      if (generateRtsData?.additionalOptions?.removeCellPoints) {
        generateRtsData.additionalOptions.removeCellPoints?.map(
          el => (el.startRow = startIndex)
        );
      }
    });

    if (this.dateTimeService.getNowDateStringFormat()) {
      generateRtsData.cellDataArray.push({
        address: 'E5',
        value: `${this.dateTimeService.getNowDateStringFormat()}`,
        name: 'create-date',
        pageNumber: 2
      });
    }

    if (order.freightName) {
      generateRtsData.cellDataArray.push({
        address: 'E6',
        value: DocumentFieldFormatter.safeValue(order.freightName),
        name: 'freight-name',
        pageNumber: 2
      });
    }

    if (order.forwarder?.orgName) {
      const forwarderData = DocumentFieldFormatter.safeConcatenate([
        { value: order.forwarder.orgName },
        { value: order.forwarder.address },
        { value: order.forwarder.phone }
      ], ', ');

      generateRtsData.cellDataArray.push({
        address: 'E7',
        value: `${forwarderData} , через агента ООО "Умные Зерна", ИНН 6154144532, 347900, Ростовская обл, г Таганрог, ул Петровская, д 51, помещ 2`,
        name: 'forwarder-data',
        pageNumber: 2
      });
    }

    if (order.forwarder?.orgName) {
      generateRtsData.cellDataArray.push({
        address: 'E8',
        value: DocumentFieldFormatter.safeValue(order.forwarder.orgName),
        name: 'forwarder-data',
        pageNumber: 2
      });
    }

    if (order.shipmentDates) {
      generateRtsData.cellDataArray.push({
        address: 'E9',
        value: `${this.dateTimeService.formatDate(order.shipmentDates[0])}`,
        name: 'start-day',
        pageNumber: 2
      });
    }

    if (order.loading.address) {
      generateRtsData.cellDataArray.push({
        address: 'E10',
        value: DocumentFieldFormatter.safeValue(order.loading.address),
        name: 'order-loading-address',
        pageNumber: 2
      });
    }

    if (order.unloading.address) {
      generateRtsData.cellDataArray.push({
        address: 'E11',
        value: DocumentFieldFormatter.safeValue(order.unloading.address),
        name: 'order-unloading-address',
        pageNumber: 2
      });
    }

    if (order.distance) {
      generateRtsData.cellDataArray.push({
        address: 'E12',
        value: DocumentFieldFormatter.safeValue(order.distance),
        name: 'distance',
        pageNumber: 2
      });
    }

    order.generateRTSActions(generateRtsData.cellDataArray);

    return generateRtsData;
  }
}
